import {
  isTerminalToolName,
  type ExternalToolResult,
  type TerminalOutputItem,
} from '@terra/shared-dto-chat-types';
import type {
  ExternalToolBatch,
  NestedToolCallIdSets,
  NestedToolCallTimeouts,
} from './external-tool-batch.types';
import {
  getBatchAliasIds,
  getBatchForCompleteRequest,
  getLiveBatchAliasId,
  isBatchWorkCurrent,
  isToolCallIdKnownToBatch,
} from './external-tool-batch-gates';
import {
  addNestedToolCallId,
  armLostExecuteTimer,
  clearLostExecuteTimer,
  clearLostExecuteTimersForBatch,
  hasNestedToolCallId,
  LOST_EXECUTE_TIMEOUT_MESSAGE,
  syncLostExecuteTimers,
} from './external-tool-batch-waiters';
import { findKnownChatBatch } from './external-tool-batch-open';

export function isIgnoredExecute({
  batches,
  ignoredExecuteToolCallIds,
  batchId,
  toolCallId,
}: {
  batches: Map<string, ExternalToolBatch>;
  ignoredExecuteToolCallIds: NestedToolCallIdSets;
  batchId: string;
  toolCallId: string;
}): boolean {
  for (const aliasId of getBatchAliasIds({ batches, batchId })) {
    if (
      hasNestedToolCallId({
        root: ignoredExecuteToolCallIds,
        batchId: aliasId,
        toolCallId,
      })
    ) {
      return true;
    }
  }

  return false;
}

export function markIgnoredExecute({
  batches,
  ignoredExecuteToolCallIds,
  batchId,
  toolCallId,
}: {
  batches: Map<string, ExternalToolBatch>;
  ignoredExecuteToolCallIds: NestedToolCallIdSets;
  batchId: string;
  toolCallId: string;
}): void {
  for (const aliasId of getBatchAliasIds({ batches, batchId })) {
    addNestedToolCallId({
      root: ignoredExecuteToolCallIds,
      batchId: aliasId,
      toolCallId,
    });
  }
}

export function clearLostExecuteForToolCall({
  batches,
  lostExecuteTimers,
  batchId,
  toolCallId,
}: {
  batches: Map<string, ExternalToolBatch>;
  lostExecuteTimers: NestedToolCallTimeouts;
  batchId: string;
  toolCallId: string;
}): void {
  for (const aliasId of getBatchAliasIds({ batches, batchId })) {
    batches.get(aliasId)?.lostExecuteToolCallIds.delete(toolCallId);
    clearLostExecuteTimer({
      timers: lostExecuteTimers,
      batchId: aliasId,
      toolCallId,
    });
  }
}

export function resolveLostExecuteBatch({
  batches,
  batchId,
  toolCallId,
}: {
  batches: Map<string, ExternalToolBatch>;
  batchId: string;
  toolCallId: string;
}): ExternalToolBatch | undefined {
  const byId = batches.get(batchId);

  if (byId?.lostExecuteToolCallIds.has(toolCallId)) {
    return byId;
  }

  for (const batch of new Set(batches.values())) {
    if (batch.lostExecuteToolCallIds.has(toolCallId)) {
      return batch;
    }
  }

  return byId;
}

export function rememberStoppedToolCallIds({
  stoppedToolCallIds,
  batches,
  chatId,
}: {
  stoppedToolCallIds: Map<string, Set<string>>;
  batches: Map<string, ExternalToolBatch>;
  chatId: string;
}): void {
  const chatToolCallIds = [...new Set(batches.values())]
    .filter((batch) => batch.chatId === chatId)
    .flatMap((batch) => [
      ...batch.requests.keys(),
      ...batch.expectedToolCallIds,
      ...batch.pendingExecuteToolCallIds,
      ...batch.lostExecuteToolCallIds,
      ...batch.results.keys(),
    ]);

  stoppedToolCallIds.set(
    chatId,
    new Set([...(stoppedToolCallIds.get(chatId) ?? []), ...chatToolCallIds]),
  );
}

export function resolveCompleteRequestBatch({
  batches,
  handlerWorkTokenByBatchId,
  workTokenByChatId,
  batchId,
  chatId,
  toolCallId,
}: {
  batches: Map<string, ExternalToolBatch>;
  handlerWorkTokenByBatchId: Map<string, number>;
  workTokenByChatId: Map<string, number>;
  batchId: string;
  chatId: string;
  toolCallId: string;
}): ExternalToolBatch | undefined {
  const batchById = getBatchForCompleteRequest({
    batches,
    handlerWorkTokenByBatchId,
    workTokenByChatId,
    batchId,
    chatId,
  });

  if (
    batchById &&
    isToolCallIdKnownToBatch({
      batch: batchById,
      toolCallId,
    })
  ) {
    return batchById;
  }

  const known = findKnownChatBatch({
    batches,
    workTokenByChatId,
    chatId,
    toolCallId,
  });

  if (known) {
    return known;
  }

  const lost = resolveLostExecuteBatch({ batches, batchId, toolCallId });

  if (lost && isToolCallIdKnownToBatch({ batch: lost, toolCallId })) {
    return lost;
  }

  return undefined;
}

export function takeLostExecuteResult({
  batches,
  workTokenByChatId,
  ignoredExecuteToolCallIds,
  batchId,
  toolCallId,
  toolName,
}: {
  batches: Map<string, ExternalToolBatch>;
  workTokenByChatId: Map<string, number>;
  ignoredExecuteToolCallIds: NestedToolCallIdSets;
  batchId: string;
  toolCallId: string;
  toolName: string;
}): ExternalToolResult | undefined {
  const batch = resolveLostExecuteBatch({ batches, batchId, toolCallId });
  const liveBatchId =
    batch === undefined
      ? undefined
      : (getLiveBatchAliasId({ batches, batch }) ?? batchId);

  if (
    !batch ||
    liveBatchId === undefined ||
    !isBatchWorkCurrent({ batch, workTokenByChatId })
  ) {
    return undefined;
  }

  if (
    batch.requests.has(toolCallId) ||
    batch.pendingExecuteToolCallIds.has(toolCallId) ||
    batch.results.has(toolCallId)
  ) {
    return undefined;
  }

  batch.expectedToolCallIds.add(toolCallId);
  markIgnoredExecute({
    batches,
    ignoredExecuteToolCallIds,
    batchId: liveBatchId,
    toolCallId,
  });

  return buildLostExecuteResult({
    batchId: batch.flushBatchId,
    chatId: batch.chatId,
    toolCallId,
    toolName: batch.toolNameByToolCallId.get(toolCallId) ?? toolName,
  });
}

export function collectArrivedExecuteToolCallIds({
  batch,
  toolCallIds,
}: {
  batch: ExternalToolBatch;
  toolCallIds: readonly string[];
}): Set<string> {
  return new Set(
    toolCallIds.filter(
      (toolCallId) =>
        batch.pendingExecuteToolCallIds.has(toolCallId) ||
        batch.requests.has(toolCallId) ||
        batch.results.has(toolCallId),
    ),
  );
}

export function armLostExecuteForMissingExecutes({
  batches,
  lostExecuteTimers,
  ignoredExecuteToolCallIds,
  batch,
  batchId,
  toolCallIds,
  arrivedToolCallIds,
  toolName,
  onLost,
}: {
  batches: Map<string, ExternalToolBatch>;
  lostExecuteTimers: NestedToolCallTimeouts;
  ignoredExecuteToolCallIds?: NestedToolCallIdSets;
  batch: ExternalToolBatch;
  batchId: string;
  toolCallIds: readonly string[];
  arrivedToolCallIds?: ReadonlySet<string>;
  toolName: string;
  onLost: (params: {
    batchId: string;
    toolCallId: string;
    toolName: string;
  }) => void;
}): void {
  for (const toolCallId of toolCallIds) {
    const executeAlreadyArrived =
      (arrivedToolCallIds?.has(toolCallId) ?? false) ||
      batch.pendingExecuteToolCallIds.has(toolCallId) ||
      batch.requests.has(toolCallId) ||
      batch.results.has(toolCallId);

    if (
      executeAlreadyArrived ||
      (ignoredExecuteToolCallIds !== undefined &&
        isIgnoredExecute({
          batches,
          ignoredExecuteToolCallIds,
          batchId,
          toolCallId,
        }))
    ) {
      clearLostExecuteForToolCall({
        batches,
        lostExecuteTimers,
        batchId,
        toolCallId,
      });
      continue;
    }

    batch.lostExecuteToolCallIds.add(toolCallId);
    armLostExecuteTimer({
      timers: lostExecuteTimers,
      batchId,
      toolCallId,
      onLost: () => {
        onLost({
          batchId,
          toolCallId,
          toolName: batch.toolNameByToolCallId.get(toolCallId) ?? toolName,
        });
      },
    });
  }
}

export function holdMissingDeclaredExecutes({
  batch,
  toolCallIds,
  arrivedToolCallIds,
}: {
  batch: ExternalToolBatch;
  toolCallIds: readonly string[];
  arrivedToolCallIds: ReadonlySet<string>;
}): void {
  for (const toolCallId of arrivedToolCallIds) {
    batch.pendingExecuteToolCallIds.add(toolCallId);
  }

  for (const toolCallId of toolCallIds) {
    if (
      arrivedToolCallIds.has(toolCallId) ||
      batch.pendingExecuteToolCallIds.has(toolCallId) ||
      batch.requests.has(toolCallId) ||
      batch.results.has(toolCallId)
    ) {
      continue;
    }

    batch.lostExecuteToolCallIds.add(toolCallId);
  }
}

export function syncLostExecuteForBatch({
  batches,
  lostExecuteTimers,
  batchId,
}: {
  batches: Map<string, ExternalToolBatch>;
  lostExecuteTimers: NestedToolCallTimeouts;
  batchId: string;
}): void {
  const batch = batches.get(batchId);

  if (!batch) {
    clearLostExecuteTimersForBatch({
      timers: lostExecuteTimers,
      batchId,
    });

    return;
  }

  for (const toolCallId of [...batch.lostExecuteToolCallIds]) {
    if (
      batch.pendingExecuteToolCallIds.has(toolCallId) ||
      batch.requests.has(toolCallId) ||
      batch.results.has(toolCallId)
    ) {
      clearLostExecuteForToolCall({
        batches,
        lostExecuteTimers,
        batchId,
        toolCallId,
      });
    }
  }

  for (const aliasId of getBatchAliasIds({ batches, batchId })) {
    syncLostExecuteTimers({
      timers: lostExecuteTimers,
      batchId: aliasId,
      pendingExecuteToolCallIds: batch.pendingExecuteToolCallIds,
      requestToolCallIds: new Set(batch.requests.keys()),
      resultToolCallIds: new Set(batch.results.keys()),
    });
  }
}

export function armLostExecuteForWaitingChatBatches({
  batches,
  inFlightFlushes,
  skipBatch,
  chatId,
  lostExecuteTimers,
  ignoredExecuteToolCallIds,
  onLost,
}: {
  batches: Map<string, ExternalToolBatch>;
  inFlightFlushes: Map<string, Promise<void>>;
  skipBatch: ExternalToolBatch | undefined;
  chatId: string;
  lostExecuteTimers: NestedToolCallTimeouts;
  ignoredExecuteToolCallIds?: NestedToolCallIdSets;
  onLost: (params: {
    batchId: string;
    toolCallId: string;
    toolName: string;
  }) => void;
}): void {
  const seen = new Set<ExternalToolBatch>();

  for (const batch of batches.values()) {
    if (batch.chatId !== chatId || batch === skipBatch || seen.has(batch)) {
      continue;
    }

    seen.add(batch);

    let inFlight = false;

    for (const [aliasId, candidate] of batches) {
      if (candidate === batch && inFlightFlushes.has(aliasId)) {
        inFlight = true;
        break;
      }
    }

    if (inFlight) {
      continue;
    }

    const liveBatchId = getLiveBatchAliasId({ batches, batch });

    if (!liveBatchId) {
      continue;
    }

    armLostExecuteForMissingExecutes({
      batches,
      lostExecuteTimers,
      ignoredExecuteToolCallIds,
      batch,
      batchId: liveBatchId,
      toolCallIds: [...batch.expectedToolCallIds],
      toolName: '',
      onLost,
    });
    syncLostExecuteForBatch({
      batches,
      lostExecuteTimers,
      batchId: liveBatchId,
    });
  }
}

export function buildLostExecuteResult({
  batchId,
  chatId,
  toolCallId,
  toolName,
}: {
  batchId: string;
  chatId: string;
  toolCallId: string;
  toolName: string;
}): ExternalToolResult {
  if (isTerminalToolName(toolName)) {
    const result: TerminalOutputItem = {
      batchId,
      toolCallId,
      command: '',
      exitCode: -1,
      output: LOST_EXECUTE_TIMEOUT_MESSAGE,
      outputLength: LOST_EXECUTE_TIMEOUT_MESSAGE.length,
      failureKind: 'timeout',
    };

    return {
      batchId,
      chatId,
      toolCallId,
      toolName,
      status: 'error',
      result,
    };
  }

  return {
    batchId,
    chatId,
    toolCallId,
    toolName,
    status: 'error',
    result: { message: LOST_EXECUTE_TIMEOUT_MESSAGE },
  };
}
